// "Compre o look": cada cartão é uma mini compra. Aqui só se acha a
// variante da combinação escolhida e se atualiza o cartão; adicionar é
// trabalho do cart.js, que intercepta o form.product-form e abre a sacola.

class BundleCard extends HTMLElement {
  connectedCallback() {
    if (this.variants) return;
    try {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    } catch {
      return;
    }
    this.sizeIndex = this.dataset.sizeIndex === '' ? null : Number(this.dataset.sizeIndex);
    this.sizeTouched = false;

    this.listeners = new AbortController();
    const { signal } = this.listeners;
    this.addEventListener('change', this.handleChange, { signal });

    // O tamanho escolhido no produto da página vale para o look também,
    // até a cliente mexer no tamanho deste cartão.
    const group = this.closest('[data-size-group]')?.dataset.sizeGroup;
    if (group && this.sizeIndex !== null) {
      document.addEventListener('change', (event) => {
        const input = event.target;
        if (this.sizeTouched || input.name !== group) return;
        this.pickSize(input.value);
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.listeners?.abort();
  }

  handleChange = (event) => {
    const input = event.target.closest('.bundle-card__input');
    if (!input) return;
    // O change do cartão não pode subir até o <product-page>, que trocaria
    // a variante do produto principal.
    event.stopPropagation();
    const group = input.closest('[data-option-index]');
    if (Number(group?.dataset.optionIndex) === this.sizeIndex) this.sizeTouched = true;
    this.update(Number(group?.dataset.optionIndex));
  };

  pickSize(value) {
    const group = this.querySelector(`[data-option-index="${this.sizeIndex}"]`);
    const input = Array.from(group?.querySelectorAll('.bundle-card__input') || []).find((el) => el.value === value);
    if (!input || input.disabled) return;
    input.checked = true;
    this.update(this.sizeIndex);
  }

  selected() {
    return Array.from(this.querySelectorAll('[data-option-index]')).map(
      (group) => group.querySelector('.bundle-card__input:checked')?.value ?? null,
    );
  }

  // changedIndex: a opção que a cliente acabou de trocar. Se a combinação
  // não existe (tamanho que falta na cor nova), fica a cor e o tamanho
  // pula para o primeiro disponível nela.
  update(changedIndex) {
    let values = this.selected();
    let variant = this.find(values);

    if (!variant?.available) {
      const fallback = this.variants.find((v) => v.available && v.options[changedIndex] === values[changedIndex]);
      if (fallback) {
        variant = fallback;
        values = fallback.options.slice();
        this.querySelectorAll('[data-option-index]').forEach((group, index) => {
          group.querySelectorAll('.bundle-card__input').forEach((el) => { el.checked = el.value === values[index]; });
        });
      }
    }

    this.refreshAvailability(values);
    this.render(variant, values);
  }

  find(values) {
    return this.variants.find((v) => v.options.every((option, index) => values[index] === null || option === values[index]));
  }

  // Um valor fica habilitado se existe variante à venda com ele e com as
  // outras opções como estão agora.
  refreshAvailability(values) {
    this.querySelectorAll('[data-option-index]').forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      group.querySelectorAll('.bundle-card__input').forEach((el) => {
        const exists = this.variants.some((v) => v.available && v.options[index] === el.value
          && v.options.every((option, other) => other === index || option === values[other]));
        el.disabled = !exists;
      });
    });
  }

  render(variant, values) {
    this.querySelectorAll('[data-option-index]').forEach((group, index) => {
      const label = group.querySelector('[data-selected]');
      if (label) label.textContent = values[index] ?? '';
    });

    const button = this.querySelector('[data-add]');
    const id = this.querySelector('[data-id]');
    if (!variant) {
      if (button) button.disabled = true;
      return;
    }
    if (id) id.value = variant.id;
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.labelAdd : button.dataset.labelSold;
    }

    const price = this.querySelector('[data-price]');
    if (price) {
      price.replaceChildren();
      if (variant.compare) {
        const s = document.createElement('s');
        s.className = 'bundle-card__compare';
        s.textContent = variant.compare;
        price.append(s);
      }
      const span = document.createElement('span');
      span.textContent = variant.price;
      price.append(span);
    }

    const image = this.querySelector('.bundle-card__image');
    if (image && variant.image && image.getAttribute('src') !== variant.image) {
      image.setAttribute('data-loading', '');
      image.removeAttribute('srcset');
      image.onload = () => image.removeAttribute('data-loading');
      image.src = variant.image;
    }
  }
}

if (!customElements.get('bundle-card')) {
  customElements.define('bundle-card', BundleCard);
}
